import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, TrendingUp, Heart, Lock, ShoppingBag, Briefcase, Users, AlertOctagon } from 'lucide-react';
import { ScamProtocol } from '../types';
import { useLanguage } from '../hooks/useLanguage';
import { Card } from './Card';
const ICONS: Record<string, React.ElementType> = {
  Users,
  TrendingUp,
  Heart,
  Lock,
  ShoppingBag,
  Briefcase,
  Shield,
  'social-engineering': Users,
  'investment-crypto': TrendingUp,
  'romance-scam': Heart,
  'phishing-account-takeover': Lock,
  'marketplace-payment': ShoppingBag,
  'job-scam': Briefcase
};
const COLORS: Record<string, string> = {
  Users: 'text-blue-600 bg-blue-50 group-hover:bg-blue-100',
  TrendingUp: 'text-green-600 bg-green-50 group-hover:bg-green-100',
  Heart: 'text-pink-600 bg-pink-50 group-hover:bg-pink-100',
  Lock: 'text-red-600 bg-red-50 group-hover:bg-red-100',
  ShoppingBag: 'text-purple-600 bg-purple-50 group-hover:bg-purple-100',
  Briefcase: 'text-orange-600 bg-orange-50 group-hover:bg-orange-100',
  Shield: 'text-primary-600 bg-primary-50 group-hover:bg-primary-100'
};
interface ProtocolCardProps {
  protocol: ScamProtocol;
}
export function ProtocolCard({
  protocol
}: ProtocolCardProps) {
  const {
    language
  } = useLanguage();
  // Map icon string to component, fallback to Shield
  const IconComponent = ICONS[protocol.icon] || ICONS[protocol.id] || Shield;
  // Get color classes based on icon name or default
  const colorClass = COLORS[protocol.icon] || COLORS['Shield'];
  const title = protocol.title[language] || protocol.title['en'];
  const description = protocol.description[language] || protocol.description['en'];
  return <Link to={`/${language}/protocols/${protocol.id}`} className="block h-full group" data-id="element-216">
      <Card className="h-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border-transparent hover:border-primary-200" data-id="element-217">
        <div className="flex flex-col h-full" data-id="element-218">
          <div className="flex items-start justify-between mb-4" data-id="element-219">
            <div className={`p-3 rounded-xl transition-colors duration-300 ${colorClass}`} data-id="element-220">
              <IconComponent className="h-6 w-6" data-id="element-221" />
            </div>

            {/* Optional: Add a badge if needed based on protocol data */}
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-222">
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-600" data-id="element-223">
                Protocol
              </span>
            </div>
          </div>

          <div className="flex-1" data-id="element-224">
            <h3 className="font-bold text-lg mb-2 text-slate-900 group-hover:text-primary-600 transition-colors line-clamp-2" data-id="element-225">
              {title}
            </h3>
            <p className="text-sm text-slate-600 line-clamp-3 mb-4 leading-relaxed" data-id="element-226">
              {description}
            </p>
          </div>

          <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between" data-id="element-227">
            <div className="flex items-center text-sm font-semibold text-primary-600 group-hover:text-primary-700 transition-colors" data-id="element-228">
              View Protocol
              <ArrowRight className="ml-1.5 h-4 w-4 transform group-hover:translate-x-1 transition-transform duration-300" data-id="element-229" />
            </div>

            {/* Warning signs count indicator */}
            {protocol.warningSigns && <div className="flex items-center text-xs text-slate-400" title="Warning signs" data-id="element-230">
                <AlertOctagon className="w-3 h-3 mr-1" data-id="element-231" />
                {Object.keys(protocol.warningSigns).length > 0 ? 'Signs' : ''}
              </div>}
          </div>
        </div>
      </Card>
    </Link>;
}